import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
// Removed admin components to avoid permission errors
import Modal from '../components/Modal';
import SupervisorDashboardCharts from '../components/SupervisorDashboardCharts';
import SupervisorTopProducts from '../components/SupervisorTopProducts';
import SupervisorSalesBySeller from '../components/SupervisorSalesBySeller';
import ChatWidget from '../components/ChatWidget'; // Asistente IA
import '../styles/Dashboard.css';
import '../styles/SupervisorDashboard.css';
import '../styles/Modal.css';
import '../styles/DashboardButtons.css';

const SupervisorDashboard = () => {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  
  // Estados principales
  const [supervisorInfo, setSupervisorInfo] = useState(null);
  const [business, setBusiness] = useState(null);
  const [pointsOfSale, setPointsOfSale] = useState([]);
  const [adminRoleStats, setAdminRoleStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [businessLoading, setBusinessLoading] = useState(false);
  const [pointsOfSaleLoading, setPointsOfSaleLoading] = useState(false);
  
  // Estados para estadísticas de ventas
  const [salesStats, setSalesStats] = useState(null);
  const [salesStatsLoading, setSalesStatsLoading] = useState(false);
  const [selectedPOS, setSelectedPOS] = useState('all');
  
  // Estados para estadísticas específicas del supervisor
  const [supervisorSalesStats, setSupervisorSalesStats] = useState(null);
  const [supervisorSalesStatsLoading, setSupervisorSalesStatsLoading] = useState(false);
  
  // Estado para ventas recientes y productos top
  const [recentSales, setRecentSales] = useState([]);
  const [recentSalesLoading, setRecentSalesLoading] = useState(false);
  const [topProducts, setTopProducts] = useState([]);
  const [topProductsLoading, setTopProductsLoading] = useState(false);
  
  // Estado para el modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [modalType, setModalType] = useState('error');
  
  // Estados para controlar habilitación de menús
  const [hasActivePointsOfSale, setHasActivePointsOfSale] = useState(true);

  // Fetch supervisor information and assignment
  const fetchSupervisorInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/supervisors/info', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSupervisorInfo(response.data);
      
      // Si es supervisor de POS específico, establecer el POS seleccionado
      if (!response.data.is_general_supervisor && response.data.pos_id) {
        setSelectedPOS(response.data.pos_id);
      }
    } catch (error) {
      console.error('Error fetching supervisor info:', error);
      setModalTitle('Error');
      setModalMessage('Error al obtener información del supervisor');
      setModalType('error');
      setModalOpen(true);
    }
  };

  // Fetch business data
  const fetchBusinessData = async () => {
    try {
      setBusinessLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/supervisors/business', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusiness(response.data);
    } catch (error) {
      console.error('Error fetching business data:', error);
    } finally {
      setBusinessLoading(false);
    }
  };

  // Fetch supervisor sales statistics
  const fetchSupervisorSalesStats = async () => {
    try {
      setSupervisorSalesStatsLoading(true);
      const token = localStorage.getItem('token');
      
      // Build URL with POS filter if selected
      const params = new URLSearchParams();
      if (selectedPOS && selectedPOS !== 'all') {
        params.append('pos_id', selectedPOS);
      }
      
      const url = `/api/supervisors/sales-stats${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSupervisorSalesStats(response.data);
    } catch (error) {
      console.error('Error fetching supervisor sales stats:', error);
    } finally {
      setSupervisorSalesStatsLoading(false);
    }
  };

  // Fetch points of sale
  const fetchPointsOfSale = async () => {
    try {
      setPointsOfSaleLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/supervisors/points-of-sale', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPointsOfSale(response.data);
      
      // Verificar si hay puntos de venta activos
      const activePoints = response.data.filter(pos => !pos.pos_closed);
      setHasActivePointsOfSale(activePoints.length > 0);
    } catch (error) {
      console.error('Error fetching points of sale:', error);
    } finally {
      setPointsOfSaleLoading(false);
    }
  };

  // Fetch admin role stats (adapted for supervisors)
  const fetchSupervisorStats = async () => {
    try {
      setStatsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/supervisors/stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminRoleStats(response.data);
    } catch (error) {
      console.error('Error fetching supervisor stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  // Fetch sales stats with POS filtering
  const fetchSalesStats = async (posId = null) => {
    try {
      setSalesStatsLoading(true);
      const token = localStorage.getItem('token');
      const url = posId && posId !== 'all' 
        ? `/api/supervisors/sales-stats?pos_id=${posId}`
        : '/api/supervisors/sales-stats';
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSalesStats(response.data);
    } catch (error) {
      console.error('Error fetching sales stats:', error);
    } finally {
      setSalesStatsLoading(false);
    }
  };

  // Fetch recent sales
  const fetchRecentSales = async () => {
    try {
      setRecentSalesLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/supervisors/recent-sales', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentSales(response.data);
    } catch (error) {
      console.error('Error fetching recent sales:', error);
    } finally {
      setRecentSalesLoading(false);
    }
  };

  // Fetch top products
  const fetchTopProducts = async () => {
    try {
      setTopProductsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/supervisors/top-products', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTopProducts(response.data);
    } catch (error) {
      console.error('Error fetching top products:', error);
    } finally {
      setTopProductsLoading(false);
    }
  };

  // Handle POS change for filtering
  const handlePOSChange = (e) => {
    const posId = e.target.value;
    setSelectedPOS(posId);
    fetchSalesStats(posId);
  };

  // Navigation handlers (without supervisor creation options)
  const handleRegistrarVentaClick = () => {
    if (!hasActivePointsOfSale) {
      setModalTitle('Puntos de Venta Cerrados');
      setModalMessage('No puedes registrar ventas porque todos los puntos de venta están cerrados.');
      setModalType('error');
      setModalOpen(true);
      return;
    }
    navigate('/supervisor-sales');
  };

  const handleRegistrarGastoClick = (e) => {
    e.preventDefault();
    if (!hasActivePointsOfSale) {
      setModalTitle('Puntos de Venta Cerrados');
      setModalMessage('No puedes registrar gastos porque todos los puntos de venta están cerrados.');
      setModalType('error');
      setModalOpen(true);
      return;
    }
    navigate('/supervisor-register-expense');
  };

  const handleReportesClick = () => {
    navigate('/reports');
  };

  const handleProductosClick = () => {
    if (!hasActivePointsOfSale) {
      setModalTitle('Puntos de Venta Cerrados');
      setModalMessage('No puedes gestionar productos porque todos los puntos de venta están cerrados.');
      setModalType('error');
      setModalOpen(true);
      return;
    }
    navigate('/products');
  };

  const handleClientesClick = () => {
    if (!hasActivePointsOfSale) {
      setModalTitle('Puntos de Venta Cerrados');
      setModalMessage('No puedes gestionar clientes porque todos los puntos de venta están cerrados.');
      setModalType('error');
      setModalOpen(true);
      return;
    }
    navigate('/customers');
  };

  const handleGastosClick = () => {
    navigate('/supervisor-expense-types');
  };

  const handleCreateSellerClick = () => {
    if (!hasActivePointsOfSale) {
      setModalTitle('Puntos de Venta Cerrados');
      setModalMessage('No puedes crear vendedores porque todos los puntos de venta están cerrados.');
      setModalType('error');
      setModalOpen(true);
      return;
    }
    // Navegar a la página de creación de vendedores con restricción de rol
    navigate('/create-seller');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Effects
  useEffect(() => {
    if (user && (user.role === 'manager' || user.role === 'admin')) {
      fetchSupervisorInfo();
      fetchBusinessData();
      fetchPointsOfSale();
      fetchSupervisorSalesStats();
    }
  }, [user]);

  // Update sales stats when POS filter changes
  useEffect(() => {
    if (user && (user.role === 'manager' || user.role === 'admin') && supervisorInfo) {
      fetchSupervisorSalesStats();
    }
  }, [selectedPOS]);

  // Redirect if not supervisor
  useEffect(() => {
    if (!loading && (!user || (user.role !== 'manager' && user.role !== 'admin'))) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  if (loading) {
    return <div className="loading">Cargando...</div>;
  }

  if (!user || (user.role !== 'manager' && user.role !== 'admin')) {
    return null;
  }


  return (
    <>
    <div className="dashboard-container">
      <header className="dashboard-header">
        <h1>Dashboard de Supervisor</h1>
        <div className="header-actions">
          <span className="user-info">
            {user.full_name} - {supervisorInfo?.is_general_supervisor ? 'Supervisor General' : 'Supervisor de POS'}
          </span>
          <button onClick={handleLogout} className="logout-btn">
            Cerrar Sesión
          </button>
        </div>
      </header>

      {/* Mensaje informativo cuando no hay puntos de venta activos */}
      {!hasActivePointsOfSale && (
        <div className="info-message warning">
          <p>
            <strong>Importante:</strong> No tiene puntos de venta abiertos. Para acceder a todas las funcionalidades, debe contactar al administrador para abrir al menos un punto de venta.
          </p>
        </div>
      )}

      {/* Botones destacados para supervisores */}
      <div className="highlighted-actions">
        <button onClick={handleRegistrarVentaClick} className={`action-button sales-button ${!hasActivePointsOfSale ? 'disabled' : ''}`} disabled={!hasActivePointsOfSale}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="9" cy="21" r="1"></circle>
            <circle cx="20" cy="21" r="1"></circle>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
          </svg>
          Registrar Ventas
        </button>
        <button onClick={handleRegistrarGastoClick} className={`action-button expenses-button ${!hasActivePointsOfSale ? 'disabled' : ''}`} disabled={!hasActivePointsOfSale}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="4" width="20" height="16" rx="2"></rect>
            <line x1="2" y1="10" x2="22" y2="10"></line>
            <line x1="6" y1="14" x2="6" y2="18"></line>
            <line x1="12" y1="14" x2="12" y2="18"></line>
            <line x1="18" y1="14" x2="18" y2="18"></line>
          </svg>
          Registrar Gastos
        </button>
      </div>

      {/* Menú de navegación para supervisores */}
      <nav className="admin-menu">
        <ul className="admin-menu-list">
          <li className="admin-menu-item">
            <button onClick={handleProductosClick} className={`admin-menu-link ${!hasActivePointsOfSale ? 'disabled' : ''}`} disabled={!hasActivePointsOfSale} title={!hasActivePointsOfSale ? 'No disponible - Puntos de venta cerrados' : 'Gestionar productos'}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <path d="M16 10a4 4 0 0 1-8 0"></path>
              </svg>
              Productos
            </button>
          </li>
          <li className="admin-menu-item">
            <button onClick={handleClientesClick} className={`admin-menu-link ${!hasActivePointsOfSale ? 'disabled' : ''}`} disabled={!hasActivePointsOfSale} title={!hasActivePointsOfSale ? 'No disponible - Puntos de venta cerrados' : 'Gestionar clientes'}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              Clientes
            </button>
          </li>
          <li className="admin-menu-item">
            <button onClick={handleGastosClick} className="admin-menu-link">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="1" x2="12" y2="23"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
              Gastos
            </button>
          </li>
          <li className="admin-menu-item">
            <button onClick={handleReportesClick} className="admin-menu-link">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                <line x1="2" y1="10" x2="22" y2="10"></line>
                <line x1="6" y1="14" x2="6" y2="18"></line>
                <line x1="12" y1="14" x2="12" y2="18"></line>
                <line x1="18" y1="14" x2="18" y2="18"></line>
              </svg>
              Reportes
            </button>
          </li>
        </ul>
      </nav>

      <div className="dashboard-content">
        <div className="user-profile-card">
          <h2>Perfil del Supervisor</h2>
          {user && (
            <div className="profile-details">
              <p><strong>Nombre:</strong> {user.full_name}</p>
              <p><strong>Email:</strong> {user.email}</p>
              <p><strong>Rol:</strong> {supervisorInfo?.is_general_supervisor ? 'Supervisor General' : 'Supervisor de POS'}</p>
              
              {/* Información de Asignación del Supervisor */}
              <div className="supervisor-assignment-details">
                <h3>Información de Asignación</h3>
                {supervisorInfo ? (
                  <>
                    <p><strong>Tipo de Supervisor:</strong> <span className="supervisor-type">{supervisorInfo.is_general_supervisor ? 'Supervisor General' : 'Supervisor de POS'}</span></p>
                    {!supervisorInfo.is_general_supervisor && supervisorInfo.pos_name && (
                      <p><strong>Punto de Venta Asignado:</strong> <span className="assigned-pos">{supervisorInfo.pos_name}</span></p>
                    )}
                    {business && (
                      <p><strong>Negocio:</strong> <span className="business-name">{business.bis_name}</span></p>
                    )}
                  </>
                ) : (
                  <p>Cargando información de asignación...</p>
                )}
              </div>
              
              {/* Estadísticas de Ventas */}
              <div className="sales-stats-details">
                <h3>Estadísticas de Ventas</h3>
                
                {/* Selector de Punto de Venta para Supervisores Generales */}
                {supervisorInfo?.is_general_supervisor && pointsOfSale.length > 1 && (
                  <div className="pos-selector">
                    <label htmlFor="pos-filter">Filtrar por Punto de Venta: </label>
                    <select 
                      id="pos-filter" 
                      value={selectedPOS} 
                      onChange={handlePOSChange}
                      className="pos-filter-select"
                    >
                      <option value="all">Todos los Puntos de Venta</option>
                      {pointsOfSale.map(pos => (
                        <option key={pos.id} value={pos.id}>{pos.pos_name}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                {supervisorSalesStatsLoading ? (
                  <p>Cargando estadísticas de ventas...</p>
                ) : supervisorSalesStats ? (
                  <div className="stats-grid">
                    <div className="stat-card">
                      <h3>Ventas del Día</h3>
                      <p className="stat-value">${supervisorSalesStats.todaySales?.toLocaleString() || '0.00'}</p>
                    </div>
                    <div className="stat-card">
                      <h3>Ventas Semana Actual</h3>
                      <p className="stat-value">${supervisorSalesStats.weekSales?.toLocaleString() || '0.00'}</p>
                    </div>
                    <div className="stat-card">
                      <h3>Ventas del Mes</h3>
                      <p className="stat-value">${supervisorSalesStats.monthSales?.toLocaleString() || '0.00'}</p>
                    </div>
                  </div>
                ) : (
                  <div className="stats-grid">
                    <div className="stat-card">
                      <h3>Ventas del Día</h3>
                      <p className="stat-value">$0.00</p>
                    </div>
                    <div className="stat-card">
                      <h3>Ventas Semana Actual</h3>
                      <p className="stat-value">$0.00</p>
                    </div>
                    <div className="stat-card">
                      <h3>Ventas del Mes</h3>
                      <p className="stat-value">$0.00</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>



        {/* Sección de Gestión de Vendedores para Supervisores - Oculta por solicitud del usuario */}
        {/* {user && user.role === 'manager' && (
          <div className="supervisor-management-section">
            <div className="seller-management-section">
              <h2>Gestión de Vendedores</h2>
              
              {business ? (
                <div className="management-card">
                  <h3>Crear Nuevo Vendedor</h3>
                  <p>Como supervisor, puedes crear usuarios vendedores para tu {supervisorInfo?.is_general_supervisor ? 'negocio' : 'punto de venta asignado'}.</p>
                  
                  <div className="seller-actions">
                    <button 
                      className="add-seller-btn"
                      onClick={() => handleCreateSellerClick()}
                      disabled={!hasActivePointsOfSale}
                      title={!hasActivePointsOfSale ? 'No disponible - Puntos de venta cerrados' : 'Crear nuevo vendedor'}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                      Crear Vendedor
                    </button>
                  </div>
                  
                  <div className="management-info">
                    <div className="info-item">
                      <strong>Permisos:</strong> Solo puedes crear usuarios con rol de vendedor
                    </div>
                    <div className="info-item">
                      <strong>Alcance:</strong> {supervisorInfo?.is_general_supervisor 
                        ? 'Vendedores para todos los puntos de venta del negocio' 
                        : `Vendedores para el punto de venta: ${supervisorInfo?.pos_name || 'Asignado'}`}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="no-business-message">
                  <p>No se encontró información del negocio.</p>
                </div>
              )}
            </div>
          </div>
        )} */}

        {/* Gráficos del Dashboard de Supervisor */}
        <SupervisorDashboardCharts selectedPOS={selectedPOS} />
        
        {/* Contenedor a ancho completo para Ventas por Vendedor */}
        <div className="full-width-section">
          {/* Ventas por Vendedor */}
          <SupervisorSalesBySeller selectedPOS={selectedPOS} />
        </div>

        {/* Productos TOP - Oculto por solicitud del usuario */}
        {/* <SupervisorTopProducts selectedPOS={selectedPOS} /> */}

      </div>

      {/* Modal for notifications */}
      <Modal 
        isOpen={modalOpen} 
        onClose={() => setModalOpen(false)} 
        title={modalTitle} 
        message={modalMessage} 
        type={modalType} 
      />
      
    </div>
    
    {/* Asistente IA - Fuera del contenedor para position fixed */}
    <ChatWidget />
    </>
  );
};

export default SupervisorDashboard;
